<template>
  <div class="order-manage-section">
    <div class="page-header">
      <h3>订单列表</h3>
      <a-range-picker v-model="orderDateRange" @change="handleOrderDateChange" style="margin-right: 16px;" />
      <a-select
        v-model="orderShopId"
        placeholder="请选择门店"
        style="width: 180px; margin-right: 16px;"
        allow-clear
        @change="handleOrderShopChange"
      >
        <a-select-option v-for="shop in shopOptions" :key="shop.id" :value="shop.id">
          {{ shop.shopName }}
        </a-select-option>
      </a-select>
      <a-input-search
        v-model="orderKeyword"
        placeholder="搜索订单号/买家"
        @search="handleOrderSearch"
        style="width: 200px;"
        allow-clear
      />
    </div>
    <a-table
      :columns="orderColumns"
      :data-source="orderList"
      :pagination="orderPagination"
      @change="handleOrderTableChange"
      :loading="orderLoading"
      rowKey="id"
    >
      <template slot="orderStatusName" slot-scope="text, record">
        <a-tag :color="getOrderStatusColorByCode(record.orderStatus)">{{ text }}</a-tag>
      </template>
      <template slot="refundStatusName" slot-scope="text, record">
        <a-tag v-if="record.refundStatus && record.refundStatus > 0" :color="getRefundStatusColor(record.refundStatus)">{{ text || '已售后' }}</a-tag>
        <span v-else>-</span>
      </template>
      <span slot="operation" slot-scope="text, record">
        <div style="white-space: nowrap;">
          <a @click="$refs.orderDetail.handleDetail(record.id)" style="margin-right: 12px;">
            <a-icon type="eye" />查看详情
          </a>
          <a @click="openRefundModal(record)" style="color: #ff4d4f;">
            <a-icon type="rollback" />退货
          </a>
        </div>
      </span>
    </a-table>

    <!-- 订单详情组件 -->
    <order-detail ref="orderDetail" />

    <!-- 退货弹窗 -->
    <refund-modal
      :visible="refundModalVisible"
      :order-data="currentRefundOrder"
      @cancel="closeRefundModal"
      @success="handleRefundSuccess"
    />
  </div>
</template>

<script>
import { listCashierOrders } from '@/api/retail/order'
import { listShop } from '@/api/shop/shop'
import OrderDetail from './modules/OrderDetail'
import RefundModal from './modules/RefundModal'

export default {
  name: 'OrderManage',
  components: {
    OrderDetail,
    RefundModal
  },
  data() {
    return {
      orderDateRange: [],
      orderKeyword: '',
      orderShopId: undefined,
      shopOptions: [],
      orderList: [],
      orderPagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      orderColumns: [
        { title: '订单编号', dataIndex: 'id', width: 120, align: 'center' },
        { title: '门店名称', dataIndex: 'shopName', width: 120, align: 'center', customRender: (text) => text || '-' },
        { title: '买家名称', dataIndex: 'buyerName', width: 100, align: 'center' },
        { title: '商品数量', dataIndex: 'goodsCount', width: 80, align: 'center' },
        { title: '实付金额', dataIndex: 'payAmount', width: 100, align: 'center', customRender: (text) => `¥${text ? Number(text).toFixed(2) : '0.00'}` },
        { title: '支付方式', dataIndex: 'payTypeName', width: 100, align: 'center' },
        { title: '订单状态', dataIndex: 'orderStatusName', width: 100, align: 'center', scopedSlots: { customRender: 'orderStatusName' } },
        { title: '售后状态', dataIndex: 'refundStatusName', width: 100, align: 'center', scopedSlots: { customRender: 'refundStatusName' } },
        { title: '下单时间', dataIndex: 'createTime', width: 160, align: 'center', customRender: (text) => text || '-' },
        { title: '操作', dataIndex: 'operation', width: 180, scopedSlots: { customRender: 'operation' }, align: 'center' }
      ],
      orderLoading: false,
      refundModalVisible: false,
      currentRefundOrder: null
    }
  },
  mounted() {
    this.loadShopOptions()
    this.loadOrderList()
  },
  methods: {
    // 加载门店下拉选项
    loadShopOptions() {
      listShop({ shopType: 3 }).then(res => {
        this.shopOptions = res.data || []
        // 当前登录用户有归属门店时，默认按本门店筛选
        const userInfo = this.$store.getters.userInfo || {}
        const currentShopId = userInfo.shopId || userInfo.storeId
        if (currentShopId && this.shopOptions.some(s => s.id === currentShopId)) {
          this.orderShopId = currentShopId
        }
      }).catch(() => {
        this.shopOptions = []
      })
    },
    handleOrderShopChange() {
      this.orderPagination.current = 1
      this.loadOrderList()
    },
    handleOrderDateChange() {
      this.orderPagination.current = 1
      this.loadOrderList()
    },
    handleOrderSearch() {
      this.orderPagination.current = 1
      this.loadOrderList()
    },
    async loadOrderList() {
      try {
        this.orderLoading = true
        const params = {
          pageNum: this.orderPagination.current,
          pageSize: this.orderPagination.pageSize,
          // goodsType: 2 // 只查询线下订单(收银台订单)
        }

        // 门店筛选
        if (this.orderShopId) {
          params.shopId = this.orderShopId
        }

        // 如果有日期范围,添加到参数中
        if (this.orderDateRange && this.orderDateRange.length === 2) {
          params.createBeginTime = this.orderDateRange[0].format('YYYY-MM-DD') + ' 00:00:00'
          params.createEndTime = this.orderDateRange[1].format('YYYY-MM-DD') + ' 23:59:59'
        }

        // 如果有搜索关键词,判断是订单编号还是买家名称
        if (this.orderKeyword && this.orderKeyword.trim()) {
          const keyword = this.orderKeyword.trim()
          // 判断是否为纯数字(订单编号)
          if (/^\d+$/.test(keyword)) {
            params.id = keyword
          } else {
            // 否则作为买家名称搜索
            params.buyerName = keyword
          }
        }

        const response = await listCashierOrders(params)
        if (response.code == 20001 && response.data) {
          this.orderList = response.data.records || []
          this.orderPagination.total = response.data.total || 0
        } else {
          this.$message.error('加载订单列表失败:' + (response.msg || '未知错误'))
        }
      } catch (error) {
        this.$message.error('加载订单列表失败')
      } finally {
        this.orderLoading = false
      }
    },
    handleOrderTableChange(pagination) {
      this.orderPagination = { ...this.orderPagination, ...pagination }
      this.loadOrderList()
    },
    getOrderStatusColorByCode(status) {
      const colorMap = {
        2010: 'orange',
        2020: 'blue',
        2030: 'cyan',
        2040: 'purple',
        2050: 'green',
        2060: 'red',
        2070: 'gray'
      }
      return colorMap[status] || 'default'
    },
    getRefundStatusColor(status) {
      const colorMap = {
        0: 'default', // 未申请
        1: 'orange',  // 买家申请
        2: 'blue',    // 卖家接受
        3: 'cyan',    // 买家发货
        4: 'purple',  // 卖家收货
        5: 'green',   // 退款成功
        6: 'red'      // 卖家拒绝
      }
      return colorMap[status] || 'default'
    },
    // 打开退货弹窗
    openRefundModal(record) {
      this.currentRefundOrder = record
      this.refundModalVisible = true
    },
    // 关闭退货弹窗
    closeRefundModal() {
      this.refundModalVisible = false
      this.currentRefundOrder = null
    },
    // 退货成功回调
    handleRefundSuccess() {
      this.refundModalVisible = false
      this.currentRefundOrder = null
      this.loadOrderList()
    }
  }
}
</script>

<style lang="less" scoped>
.order-manage-section {
  padding: 0;
}

.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
  background-color: #fff;
  border-radius: 4px;

  h3 {
    margin: 0 16px 0 0;
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
